import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { directory, orgchart } from '@cwos/api-client';
import { Pill, SuccessEmpty } from '@cwos/ui-admin';
import { mayListWorkers, usePermissions } from '../permissions.js';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import { pickerState } from '../pickerState.js';
import { personDirectory, type PersonDirectory } from '../personDirectory.js';
import { PersonChip } from '../PersonChip.js';
import { formatWhen } from './coverageWindows.js';

/**
 * EVERY CHANGE TO WHO MAY DO WHAT, INCLUDING THE ONES THAT WERE REFUSED.
 *
 * `authority_event` is the ledger the whole orgchart domain writes to: an edge
 * created or ended, a team, a relationship type, coverage assigned or revoked, a
 * gap raised, the superuser tier moving in either direction, a role composed,
 * recomposed or assigned, a permission granted — and `ceiling_refusal`, which is
 * the row for an attempt the privilege ceiling turned down.
 *
 * THE REFUSALS ARE THE POINT OF PUTTING IT ON SCREEN. A history of successful
 * grants tells a security reader what the organisation's authority looks like.
 * A history that also carries the refusals tells them who has been REACHING for
 * authority they do not have, and how often — which is the question an audit is
 * usually actually asking. `refusalCode` carries the closed reason, so
 * "somebody tried to hand out a reserved key" and "somebody tried to grant to
 * themselves" are distinguishable rows rather than one undifferentiated wall.
 *
 * NOTHING HERE IS DERIVED. Every field on every row is what the server sent;
 * the screen chooses words for the event type and renders the rest. An audit
 * surface that computed anything would be inventing evidence.
 */
export function AuthorityHistory() {
  const permissions = usePermissions();
  const canReadDirectory = mayListWorkers(permissions);
  const [subjectUserId, setSubjectUserId] = useState('');

  const workers = useQuery({
    queryKey: ['directory', 'workers', 'for-orgchart'],
    queryFn: () => directory.workers(),
    enabled: canReadDirectory,
  });
  const people = personDirectory(
    { data: workers.data, isLoading: workers.isLoading, isError: workers.isError },
    canReadDirectory,
  );
  const candidates = (workers.data ?? []).filter((w) => w.userId);

  const history = useQuery({
    queryKey: ['orgchart', 'authority-history', subjectUserId],
    queryFn: () =>
      orgchart.authorityHistory(
        subjectUserId === '' ? { limit: 100 } : { subjectUserId, limit: 100 },
      ),
  });

  const rosterWithheld = !permissions.isLoading && !permissions.isError && !canReadDirectory;
  const picker = pickerState(
    rosterWithheld
      ? { kind: 'refused' }
      : listPhase(
          permissions.isLoading || permissions.isError
            ? { isLoading: true, isError: false, error: null, data: undefined }
            : workers,
          candidates.length,
        ),
    'people',
    'Everyone',
  );

  const phase = listPhase(history, history.data?.length);

  return (
    <div className="rn-col">
      <header className="rn-col">
        <h2>Authority history</h2>
        <p className="rn-muted">
          Every change to who may do what in this organization, and every attempt the privilege
          ceiling refused. Nothing on this list is derived — each row is what the server recorded at
          the moment it happened.
        </p>
      </header>

      <label>
        About
        <select
          value={subjectUserId}
          disabled={picker.disabled}
          onChange={(e) => setSubjectUserId(e.target.value)}
        >
          <option value="">{picker.placeholder}</option>
          {candidates.map((w) => (
            <option key={w.userId!} value={w.userId!}>
              {w.displayName}
            </option>
          ))}
        </select>
      </label>
      {picker.note ? (
        <p className="rn-muted" role="status">
          {picker.note} The history itself is readable; the names in it are not.
        </p>
      ) : null}

      <ListState
        phase={phase}
        surface="table"
        rows={6}
        failedMessage="We couldn’t load the authority history just now. Do not read this as “nothing has changed” — it is a failure to ask."
        onRetry={() => void history.refetch()}
        empty={
          <SuccessEmpty
            heading="No recorded changes"
            message={
              subjectUserId === ''
                ? 'The server was asked and returned no authority events for this organization. That is a measured answer, not a blank panel.'
                : `The server was asked and returned no authority events about ${people.name(subjectUserId)}.`
            }
          />
        }
      >
        <ul className="rn-list">
          {(history.data ?? []).map((event) => (
            <AuthorityRow key={event.id} event={event} people={people} />
          ))}
        </ul>
      </ListState>
    </div>
  );
}

function AuthorityRow({
  event,
  people,
}: {
  event: orgchart.AuthorityEventView;
  people: PersonDirectory;
}) {
  const refused = event.refusalCode !== null;
  return (
    <li data-authority-event={event.eventType} data-refusal-code={event.refusalCode ?? ''}>
      <span className="rn-row">
        <Pill variant={refused ? 'urgent' : 'plain'}>{refused ? 'Refused' : 'Recorded'}</Pill>
        <strong>{eventLabel(event)}</strong>
        <span className="rn-muted">{formatWhen(event.occurredAt)}</span>
      </span>
      <span className="rn-row">
        {/* Actor and subject are separate people and separate facts. Merging
            them is how "A gave B authority" becomes "somebody did something". */}
        <PersonChip person={people.person(event.actorUserId)} detail="acting" />
        {event.subjectUserId ? (
          <PersonChip person={people.person(event.subjectUserId)} detail="about" />
        ) : null}
      </span>
      {event.effectiveFrom || event.effectiveTo ? (
        <p className="rn-muted" data-authority-window={event.effectiveTo ?? ''}>
          Window: {event.effectiveFrom ? formatWhen(event.effectiveFrom) : 'open'} →{' '}
          {event.effectiveTo ? formatWhen(event.effectiveTo) : 'open'}
        </p>
      ) : null}
      {event.reason ? <p className="rn-muted">“{event.reason}”</p> : null}
      {refused ? (
        <p className="rn-muted">
          The ceiling refused this attempt with the code <code>{event.refusalCode}</code>. Nothing
          was conferred.
        </p>
      ) : null}
      {event.superuserDesignation ? (
        <p className="rn-muted" data-superuser-direction={event.superuserDesignation}>
          {/* NOT a boolean. `'granted'` and `'revoked'` are both truthy, and
              reading this field as one turns a revocation into a designation. */}
          The superuser tier was{' '}
          {event.superuserDesignation === 'granted' ? 'conferred' : 'taken away'}.
        </p>
      ) : null}
      {permissionDeltaLine(event.permissionDelta)}
    </li>
  );
}

const EVENT_LABEL: Readonly<Record<string, string>> = Object.freeze({
  relationship_created: 'Reporting relationship created',
  relationship_ended: 'Reporting relationship ended',
  relationship_type_created: 'Relationship type defined',
  team_created: 'Team created',
  coverage_assigned: 'Coverage assigned — authority lent',
  coverage_revoked: 'Coverage revoked',
  coverage_gap_raised: 'Coverage gap raised as an alert',
  superuser_designated: 'Superuser designated',
  superuser_revoked: 'Superuser designation revoked',
  role_composed: 'Role composed',
  role_recomposed: 'Role recomposed',
  role_assigned: 'Composed role assigned to a person',
  permission_granted: 'Permission granted directly',
  ceiling_refusal: 'Attempt refused by the privilege ceiling',
});

/**
 * The row's own words, or the raw type when this build has not been taught one.
 *
 * `authority_event_type_ck` is a closed list in the database, and this map
 * mirrors it — but the fallback is the raw string rather than "Unknown event",
 * because a server that grew a fourteenth type is telling this screen something
 * real and hiding the word would hide the event.
 */
function eventLabel(event: orgchart.AuthorityEventView): string {
  return EVENT_LABEL[event.eventType] ?? event.eventType;
}

/**
 * The keys an event added or removed, when it recorded them.
 *
 * `permissionDelta` is `unknown` on the wire because the column is jsonb and
 * different event types put different shapes in it. Read defensively and render
 * NOTHING when the shape is not the one this screen knows — a guessed delta on
 * an authority ledger is worse than an absent one.
 */
function permissionDeltaLine(delta: unknown): ReactNode {
  if (typeof delta !== 'object' || delta === null) return null;
  const added = (delta as { added?: unknown }).added;
  const removed = (delta as { removed?: unknown }).removed;
  const addedKeys = Array.isArray(added) ? added.filter((k) => typeof k === 'string') : [];
  const removedKeys = Array.isArray(removed) ? removed.filter((k) => typeof k === 'string') : [];
  if (addedKeys.length === 0 && removedKeys.length === 0) return null;
  return (
    <p
      className="rn-muted"
      data-delta-added={addedKeys.length}
      data-delta-removed={removedKeys.length}
    >
      {addedKeys.length > 0 ? `Added: ${addedKeys.join(', ')}. ` : ''}
      {removedKeys.length > 0 ? `Removed: ${removedKeys.join(', ')}.` : ''}
    </p>
  );
}
